/**
 * Audit History row collapsing for the PO Line Detail tab.
 *
 * Moved verbatim out of POLineDetailTab.tsx (Customer review 11 Sep pushed that
 * file past the 500-line limit).
 */
import type { ChangelogEntry } from '../common/Changelog';

/**
 * CR-4731 round 2: collapse propose+decision row pairs into one row.
 *
 * The backend writes two POChangeLog rows per change-request lifecycle: one
 * for the proposal (orange "Change Request" badge) and one for the
 * accept/reject/cancel decision (green "Change Accepted" badge etc.). The
 * Audit History panel was rendering both, which read to users as the system
 * over-counting the number of changes (the reviewer's PRO00222 line 3: 1 request +
 * 1 acceptance = 6 audit rows for 3 fields). Group by (field, ref) and keep
 * the most recent — the decision row when present (correct old/new values
 * after CR-4731 round 1, plus the right badge), or the proposal row while
 * the request is still open. Rows without a change_request_ref (legacy data,
 * GRN events, hold/critical changes) pass through unchanged.
 *
 * @param entries - The line's audit entries, any order.
 * @returns The collapsed entries, newest first.
 */
export function collapseChangeRequestRows(entries: ChangelogEntry[]): ChangelogEntry[] {
  // First pass: bucket rows by (field, change_request_ref) so we can
  // identify pairs and pick the surviving row.
  const buckets = new Map<string, ChangelogEntry[]>();
  const passthrough: ChangelogEntry[] = [];
  for (const entry of entries) {
    if (entry.change_request_ref == null) {
      passthrough.push(entry);
      continue;
    }
    const key = `${entry.field_changed}::${entry.change_request_ref}`;
    const bucket = buckets.get(key) || [];
    bucket.push(entry);
    buckets.set(key, bucket);
  }

  // CR-4731 round 5: when a bucket has both a submission and a decision
  // row, keep the decision row as the surviving entry but attach the
  // submission event so the card shows "Submitted by X on Y" beneath
  // the outcome. Single-row buckets pass through unchanged.
  const grouped: ChangelogEntry[] = [];
  buckets.forEach(bucket => {
    const ordered = [...bucket].sort(
      (a, b) => new Date(a.change_date).getTime() - new Date(b.change_date).getTime()
    );
    const decision = [...ordered].reverse().find(r => {
      const reason = (r.reason || '').toLowerCase();
      return /\b(accepted|declined|rejected|cancelled)\b/.test(reason);
    });
    const submission = ordered.find(r => r !== decision);
    if (decision && submission) {
      grouped.push({
        ...decision,
        submission_event: {
          change_date: submission.change_date,
          changed_by: submission.changed_by,
          changed_by_company: submission.changed_by_company,
        },
      });
    } else {
      grouped.push(ordered[ordered.length - 1]);
    }
  });

  return [...grouped, ...passthrough].sort(
    (a, b) => new Date(b.change_date).getTime() - new Date(a.change_date).getTime()
  );
}
